import React from 'react';
import { getResponsiveMultiplier } from '../../utils/visualizerUtils';

export function AmbientSpectrum({ ctx, dataArray, width, height, isPlaying }) {
  if (!ctx || width <= 0 || height <= 0) return;
  
  const time = Date.now() * 0.001;
  
  // Clear canvas with dark background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
  ctx.fillRect(0, 0, width, height);
  
  // Create gradient background
  const bgGradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, width/2);
  bgGradient.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
  bgGradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, width, height);
  
  const centerX = width / 2;
  const centerY = height / 2;
  
  // Get audio data for reactive elements
  const bufferLength = dataArray ? dataArray.length : 0;
  
  // Audio-reactive spectrum bars at the bottom - optimized for performance
  if (isPlaying && dataArray && bufferLength > 0) {
    // Reduce bar count for better performance
    const maxBars = Math.min(140, bufferLength); // Increased from 80 to 140 (45% reduction)
    const step = Math.floor(bufferLength / maxBars);
    const barWidth = width / maxBars * getResponsiveMultiplier();
    let x = 0;
    
    for (let i = 0; i < maxBars; i++) {
      const dataIndex = Math.min(i * step, bufferLength - 1);
      const barHeight = (dataArray[dataIndex] / 255) * height * 0.4;
      const hue = (i / maxBars) * 360 + time * 30;
      const alpha = 0.6 + (dataArray[dataIndex] / 255) * 0.4;
      
      // Bottom spectrum bars only (no mirror effect)
      const gradient = ctx.createLinearGradient(0, height, 0, height - barHeight);
      gradient.addColorStop(0, `hsla(${hue}, 80%, 60%, ${alpha})`);
      gradient.addColorStop(0.5, `hsla(${hue + 30}, 70%, 70%, ${alpha * 0.8})`);
      gradient.addColorStop(1, `hsla(${hue + 60}, 60%, 80%, ${alpha * 0.4})`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(x, height - barHeight, barWidth - 1, barHeight);
      
      // Move to next position - this ensures bars overlap slightly for full coverage
      x += barWidth;
    }
    
    // Audio-reactive concentric circles
    for (let i = 0; i < 5; i++) {
      const audioInfluence = bufferLength > i * 10 ? (dataArray[i * 10] / 255) : 0;
      const radius = 30 + i * 25 + Math.sin(time * 2 + i) * 10 + audioInfluence * 20;
      const alpha = 0.3 - i * 0.05 + Math.sin(time * 3 + i) * 0.1 + audioInfluence * 0.3;
      const hue = (time * 50 + i * 60 + audioInfluence * 120) % 360;
      
      ctx.strokeStyle = `hsla(${hue}, 70%, 60%, ${Math.max(0, alpha)})`;
      ctx.lineWidth = 2 + audioInfluence * 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Audio-reactive floating particles
    for (let i = 0; i < 30; i++) {
      const audioIndex = Math.floor((i / 30) * bufferLength);
      const audioInfluence = bufferLength > audioIndex ? (dataArray[audioIndex] / 255) : 0;
      
      const angle = (i / 30) * Math.PI * 2 + time * 0.5 + audioInfluence * 2;
      const distance = 80 + Math.sin(time * 2 + i) * 30 + audioInfluence * 40;
      const x = centerX + Math.cos(angle) * distance;
      const y = centerY + Math.sin(angle) * distance;
      const size = 2 + Math.sin(time * 4 + i) * 1 + audioInfluence * 3;
      const hue = (time * 30 + i * 18 + audioInfluence * 180) % 360;
      const alpha = 0.6 + Math.sin(time * 3 + i) * 0.3 + audioInfluence * 0.4;
      
      ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${alpha})`;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
      
      // Add glow effect for high audio values
      if (audioInfluence > 0.7) {
        ctx.shadowColor = `hsla(${hue}, 100%, 80%, 0.8)`;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(x, y, size * 1.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }
    
    // Central pulsing core with audio reactivity
    const avgAudio = bufferLength > 0 ? dataArray.reduce((sum, val) => sum + val, 0) / bufferLength / 255 : 0;
    const coreSize = 15 + Math.sin(time * 4) * 8 + avgAudio * 25;
    const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
    const coreHue = (time * 60 + avgAudio * 180) % 360;
    
    coreGradient.addColorStop(0, `hsla(${coreHue}, 100%, 80%, ${0.8 + avgAudio * 0.2})`);
    coreGradient.addColorStop(0.5, `hsla(${coreHue + 60}, 90%, 70%, ${0.5 + avgAudio * 0.3})`);
    coreGradient.addColorStop(1, `hsla(${coreHue + 120}, 80%, 60%, 0)`);
    
    ctx.fillStyle = coreGradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, coreSize, 0, Math.PI * 2);
    ctx.fill();
    
    // Audio-reactive waves
    ctx.strokeStyle = `hsla(${(time * 40 + avgAudio * 120) % 360}, 60%, 50%, ${0.4 + avgAudio * 0.4})`;
    ctx.lineWidth = 2 + avgAudio * 2;
    ctx.beginPath();
    for (let x = 0; x <= width; x += 4) {
      const waveInfluence = bufferLength > 0 ? (dataArray[Math.floor((x / width) * bufferLength)] / 255) : 0;
      const y = centerY + 
                Math.sin(x * 0.02 + time * 2) * (30 + waveInfluence * 20) + 
                Math.sin(x * 0.01 + time) * (15 + waveInfluence * 10);
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
    
    // Additional wave layer
    ctx.strokeStyle = `hsla(${(time * 60 + avgAudio * 90) % 360}, 70%, 60%, ${0.3 + avgAudio * 0.3})`;
    ctx.lineWidth = 1 + avgAudio * 1.5;
    ctx.beginPath();
    for (let x = 0; x <= width; x += 6) {
      const waveInfluence = bufferLength > 0 ? (dataArray[Math.floor((x / width) * bufferLength)] / 255) : 0;
      const y = centerY + 
                Math.sin(x * 0.015 + time * 1.5 + Math.PI) * (20 + waveInfluence * 15) + 
                Math.sin(x * 0.008 + time * 0.8) * (10 + waveInfluence * 8);
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
    
  } else {
    // Fallback to original default animation when no audio data
    // Animated concentric circles
    for (let i = 0; i < 5; i++) {
      const radius = 30 + i * 25 + Math.sin(time * 2 + i) * 10;
      const alpha = 0.3 - i * 0.05 + Math.sin(time * 3 + i) * 0.1;
      const hue = (time * 50 + i * 60) % 360;
      
      ctx.strokeStyle = `hsla(${hue}, 70%, 60%, ${Math.max(0, alpha)})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Floating particles
    for (let i = 0; i < 20; i++) {
      const angle = (i / 20) * Math.PI * 2 + time * 0.5;
      const distance = 80 + Math.sin(time * 2 + i) * 30;
      const x = centerX + Math.cos(angle) * distance;
      const y = centerY + Math.sin(angle) * distance;
      const size = 2 + Math.sin(time * 4 + i) * 1;
      const hue = (time * 30 + i * 18) % 360;
      const alpha = 0.6 + Math.sin(time * 3 + i) * 0.3;
      
      ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${alpha})`;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Central pulsing core
    const coreSize = 15 + Math.sin(time * 4) * 8;
    const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
    const coreHue = (time * 60) % 360;
    coreGradient.addColorStop(0, `hsla(${coreHue}, 100%, 80%, 0.8)`);
    coreGradient.addColorStop(0.5, `hsla(${coreHue + 60}, 90%, 70%, 0.5)`);
    coreGradient.addColorStop(1, `hsla(${coreHue + 120}, 80%, 60%, 0)`);
    
    ctx.fillStyle = coreGradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, coreSize, 0, Math.PI * 2);
    ctx.fill();
    
    // Animated waves
    ctx.strokeStyle = `hsla(${(time * 40) % 360}, 60%, 50%, 0.4)`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = 0; x <= width; x += 4) {
      const y = centerY + Math.sin(x * 0.02 + time * 2) * 30 + Math.sin(x * 0.01 + time) * 15;
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
  }
}